import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Portal } from './Portal';

interface GozapProxyModalProps {
  sessionName: string;
  displayName: string;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  onClose: () => void;
}

type ProxyMode = 'dynamic' | 'global' | 'custom' | 'managed';

export function GozapProxyModal({ sessionName, displayName, authenticatedFetch, onClose }: GozapProxyModalProps) {
  const [mode, setMode] = useState<ProxyMode>('dynamic');
  const [proxyUrl, setProxyUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const base = `/api/waha/sessions/${encodeURIComponent(sessionName)}/gozap/proxy`;

  useEffect(() => {
    const loadCurrent = async () => {
      setLoading(true);
      try {
        const resp = await authenticatedFetch(base);
        if (resp.ok) {
          const data = await resp.json();
          if (data.mode) setMode(data.mode);
          if (data.proxy_url) setProxyUrl(data.proxy_url);
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de proxy:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCurrent();
  }, [sessionName]);

  const handleSave = async () => {
    if (mode === 'custom' && !proxyUrl.trim()) {
      toast.error('Informe a URL do proxy customizado');
      return;
    }
    setSaving(true);
    try {
      const resp = await authenticatedFetch(base, {
        method: 'POST',
        body: JSON.stringify({ mode, proxyUrl: mode === 'custom' ? proxyUrl.trim() : undefined })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error || 'Erro ao salvar configuração de proxy');
      }
      toast.success(data.restart_requested ? 'Proxy atualizado. Reconectando sessão...' : 'Proxy atualizado');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar configuração de proxy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-100 m-4" role="dialog" aria-labelledby="gozap-proxy-title">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id="gozap-proxy-title" className="text-xl font-bold text-gray-900">Configurar Proxy</h2>
              <p className="text-sm text-gray-500 mt-1">{displayName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-6 text-gray-500 text-sm">Carregando configuração atual...</div>
          ) : (
            <div className="space-y-4">
              <div>
                <label htmlFor="proxy-mode" className="block text-sm font-semibold text-gray-700 mb-2">
                  Modo de Proxy
                </label>
                <select
                  id="proxy-mode"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as ProxyMode)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={saving}
                >
                  <option value="dynamic">Dinâmico (provisionado automaticamente)</option>
                  <option value="global">Global (configurado no servidor)</option>
                  <option value="custom">Customizado (URL própria)</option>
                  <option value="managed">Gerenciado (configurado no servidor)</option>
                </select>
              </div>

              {mode === 'custom' && (
                <div>
                  <label htmlFor="proxy-url" className="block text-sm font-semibold text-gray-700 mb-2">
                    URL do Proxy
                  </label>
                  <input
                    id="proxy-url"
                    type="text"
                    value={proxyUrl}
                    onChange={(e) => setProxyUrl(e.target.value)}
                    placeholder="socks5://usuario:senha@proxy.exemplo.com:1080"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    disabled={saving}
                  />
                </div>
              )}

              <p className="text-xs text-gray-500">
                Se a sessão estiver conectada, a troca de proxy reinicia a conexão automaticamente.
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 font-medium border border-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
